import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Turtle, Users, Plus, Zap, Copy, Check, Send, Crown, Play, ArrowLeft, LogIn as LogInIcon, RefreshCw } from 'lucide-react';
import { authService } from '../services/auth';
import { useTheme } from '../contexts/ThemeContext';
import { io, Socket } from 'socket.io-client';

interface Player {
  socketId: string;
  userId: string;
  username: string;
  avatar?: string;
  points: number;
  questionCount: number;
}

interface RoomData {
  id: string;
  code: string;
  host: { socketId: string; userId: string; username: string };
  story?: { id: string; title: string; surface: string };
  players: Player[];
  status: 'waiting' | 'playing' | 'ended';
  questionCount: number;
}

interface ChatMessage {
  id?: string;
  username?: string;
  content: string;
  type?: string;
  timestamp?: string | Date;
  questionCount?: number;
}

const MultiplayerPage: React.FC = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [rooms, setRooms] = useState<RoomData[]>([]);
  const [currentRoom, setCurrentRoom] = useState<RoomData | null>(null);
  const [joinCode, setJoinCode] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [mySocketId, setMySocketId] = useState('');
  const socketRef = useRef<Socket | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const currentUser = authService.getCurrentUser();

  useEffect(() => {
    const token = authService.getToken();
    if (!token) {
      setIsLoggedIn(false);
      return;
    }
    setIsLoggedIn(true);

    const socket = io('/multiplayer', {
      path: '/api/socket.io/',
      auth: { token },
      transports: ['websocket', 'polling']
    });

    socket.on('connect', () => {
      setIsConnected(true);
      setMySocketId(socket.id || '');
      socket.emit('get-rooms');
    });

    socket.on('disconnect', () => setIsConnected(false));

    socket.on('rooms-list', (list: RoomData[]) => setRooms(list || []));

    socket.on('room-created', (room: RoomData) => {
      setCurrentRoom(room);
      setMessages([]);
      setError('');
    });

    socket.on('room-joined', (room: RoomData) => {
      setCurrentRoom(room);
      setMessages([]);
      setError('');
    });

    socket.on('room-updated', (room: RoomData) => {
      setCurrentRoom(room);
    });

    socket.on('room-error', (data: { error: string }) => {
      setError(data.error);
      setTimeout(() => setError(''), 4000);
    });

    socket.on('match-error', (data: { error: string }) => {
      setError(data.error);
      setTimeout(() => setError(''), 4000);
    });

    socket.on('system-message', (msg: ChatMessage) => {
      setMessages(prev => [...prev, { ...msg, type: msg.type || 'system' }]);
    });

    socket.on('new-message', (msg: ChatMessage) => {
      setMessages(prev => [...prev, msg]);
    });

    socket.on('player-joined', () => { /* room-updated 会同步 */ });
    socket.on('player-left', () => { /* room-updated 会同步 */ });
    socket.on('host-changed', (data: { newHost: string }) => {
      setMessages(prev => [...prev, { content: `👑 ${data.newHost} 成为新房主`, type: 'system' }]);
    });

    socket.on('game-started', (room: RoomData) => {
      setCurrentRoom(room);
    });

    socket.on('game-solved', (data: any) => {
      setMessages(prev => [...prev, {
        content: `🎉 ${data.username || '有玩家'} 猜出了真相！+${data.points || 0} 积分`,
        type: 'success'
      }]);
    });

    socketRef.current = socket;

    return () => {
      socket.disconnect();
    };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const refreshRooms = () => socketRef.current?.emit('get-rooms');

  const createRoom = () => socketRef.current?.emit('create-room', {});

  const quickMatch = () => socketRef.current?.emit('quick-match');

  const joinRoom = (code?: string) => {
    const target = (code || joinCode).trim().toUpperCase();
    if (!target) return;
    socketRef.current?.emit('join-room', { code: target });
  };

  const leaveRoom = () => {
    socketRef.current?.emit('leave-room');
    setCurrentRoom(null);
    setMessages([]);
    refreshRooms();
  };

  const startGame = () => socketRef.current?.emit('start-game');

  const sendMessage = () => {
    const text = inputMessage.trim();
    if (!text || !currentRoom) return;
    if (currentRoom.status === 'playing') {
      socketRef.current?.emit('send-message', { content: text, type: 'question' });
    } else {
      socketRef.current?.emit('send-message', { content: text, type: 'chat' });
    }
    setInputMessage('');
  };

  const submitGuess = () => {
    const text = inputMessage.trim();
    if (!text || !currentRoom) return;
    socketRef.current?.emit('submit-guess', { guess: text });
    setInputMessage('');
  };

  const copyRoomCode = () => {
    if (currentRoom) {
      navigator.clipboard.writeText(currentRoom.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const isHost = currentRoom && currentUser && currentRoom.host?.userId === currentUser.userId;

  // 未登录
  if (!isLoggedIn) {
    return (
      <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-gray-900' : 'bg-gradient-to-br from-turtle-50 to-white'}`}>
        <nav className={`sticky top-0 z-50 border-b backdrop-blur-md ${isDark ? 'bg-gray-800/80 border-gray-700' : 'bg-white/70 border-gray-200'}`}>
          <div className="container mx-auto px-4 py-4 flex items-center">
            <button onClick={() => navigate('/')} className="flex items-center space-x-2">
              <ArrowLeft className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
              <Turtle className={`w-6 h-6 ${isDark ? 'text-ocean-400' : 'text-ocean-600'}`} />
              <span className={`text-lg font-bold ${isDark ? 'text-ocean-400' : 'text-ocean-600'}`}>法底捞针</span>
            </button>
          </div>
        </nav>
        <div className="container mx-auto px-4 py-20 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <LogInIcon className="w-16 h-16 text-ocean-300 mx-auto mb-4" />
            <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>请先登录</h2>
            <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>多人联机需要登录后使用</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-ocean-600 text-white rounded-xl hover:bg-ocean-700 transition-colors"
            >
              返回首页登录
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-gray-900' : 'bg-gradient-to-br from-turtle-50 to-white'}`}>
      {/* 导航栏 */}
      <nav className={`sticky top-0 z-50 border-b backdrop-blur-md transition-colors duration-300 ${isDark ? 'bg-gray-800/80 border-gray-700' : 'bg-white/70 border-gray-200'}`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <button onClick={() => currentRoom ? leaveRoom() : navigate('/')} className="flex items-center space-x-2">
              <ArrowLeft className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
              <Turtle className={`w-6 h-6 ${isDark ? 'text-ocean-400' : 'text-ocean-600'}`} />
              <span className={`text-lg font-bold ${isDark ? 'text-ocean-400' : 'text-ocean-600'}`}>法底捞针</span>
            </button>
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {isConnected ? '已连接' : '连接中...'}
              </span>
            </div>
          </div>
        </div>
      </nav>

      {/* 错误提示 */}
      {error && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50">
          {error}
        </div>
      )}

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {!currentRoom ? (
          /* ===== 大厅 ===== */
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="text-center mb-8">
              <h1 className={`text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>
                <Users className="inline w-8 h-8 mr-2 text-purple-500" />
                多人联机
              </h1>
              <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                创建或加入房间，与好友一起推理海龟汤
              </p>
            </div>

            {/* 操作区 */}
            <div className={`mb-8 p-6 rounded-2xl ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white shadow-lg'}`}>
              <div className="grid md:grid-cols-3 gap-4">
                <button
                  onClick={createRoom}
                  disabled={!isConnected}
                  className="flex items-center justify-center px-6 py-4 bg-gradient-to-r from-purple-500 to-purple-700 text-white rounded-xl hover:from-purple-600 hover:to-purple-800 transition font-medium disabled:opacity-50"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  创建房间
                </button>
                <button
                  onClick={quickMatch}
                  disabled={!isConnected}
                  className="flex items-center justify-center px-6 py-4 bg-gradient-to-r from-ocean-500 to-ocean-700 text-white rounded-xl hover:from-ocean-600 hover:to-ocean-800 transition font-medium disabled:opacity-50"
                >
                  <Zap className="w-5 h-5 mr-2" />
                  快速匹配
                </button>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={joinCode}
                    onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                    placeholder="房间码"
                    maxLength={4}
                    className={`flex-1 px-4 py-3 rounded-xl border text-center text-lg font-mono tracking-widest ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'} focus:outline-none focus:ring-2 focus:ring-purple-500/30`}
                  />
                  <button
                    onClick={() => joinRoom()}
                    disabled={!isConnected || joinCode.trim().length < 4}
                    className="px-5 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl hover:from-green-600 hover:to-green-700 transition font-medium disabled:opacity-50"
                  >
                    加入
                  </button>
                </div>
              </div>
            </div>

            {/* 房间列表 */}
            <div className={`rounded-2xl p-6 ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white shadow-lg'}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  活跃房间 ({rooms.length})
                </h3>
                <button
                  onClick={refreshRooms}
                  className={`flex items-center px-3 py-1.5 rounded-lg text-sm transition ${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  刷新
                </button>
              </div>
              {rooms.length === 0 ? (
                <div className={`text-center py-12 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-40" />
                  <p>暂无活跃房间，创建一个吧！</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {rooms.map((room) => (
                    <motion.div
                      key={room.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className={`flex items-center justify-between p-4 rounded-xl border ${isDark ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}
                    >
                      <div>
                        <div className={`font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                          {room.story?.title || '海龟汤推理'}
                        </div>
                        <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          房主: {room.host?.username} · {room.players?.length || 0}/8 人 · 房间码 {room.code}
                        </div>
                      </div>
                      <button
                        onClick={() => joinRoom(room.code)}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition text-sm font-medium"
                      >
                        加入
                      </button>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        ) : (
          /* ===== 房间内 ===== */
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            {/* 房间信息 */}
            <div className={`mb-4 p-4 rounded-2xl flex items-center justify-between ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white shadow-lg'}`}>
              <div className="flex items-center space-x-4">
                <div>
                  <div className={`font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                    {currentRoom.story?.title || '等待开始...'}
                  </div>
                  <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {currentRoom.status === 'waiting' ? '等待房主开始' : currentRoom.status === 'playing' ? '游戏进行中' : '已结束'}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={copyRoomCode}
                  className={`flex items-center px-3 py-2 rounded-lg font-mono text-lg tracking-widest transition ${isDark ? 'bg-gray-700 text-yellow-400 hover:bg-gray-600' : 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100'}`}
                  title="点击复制房间码"
                >
                  {copied ? <Check className="w-5 h-5 mr-1" /> : <Copy className="w-5 h-5 mr-1" />}
                  {currentRoom.code}
                </button>
                {isHost && currentRoom.status === 'waiting' && (
                  <button
                    onClick={startGame}
                    className="flex items-center px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition font-medium"
                  >
                    <Play className="w-5 h-5 mr-1" />
                    开始游戏
                  </button>
                )}
              </div>
            </div>

            {/* 玩家列表 */}
            <div className={`mb-4 p-4 rounded-2xl ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white shadow-lg'}`}>
              <div className="flex flex-wrap gap-3">
                {currentRoom.players?.map((player) => (
                  <div
                    key={player.socketId}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-full ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}
                  >
                    {player.userId === currentRoom.host?.userId && (
                      <Crown className="w-4 h-4 text-yellow-500" />
                    )}
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${isDark ? 'bg-ocean-700 text-white' : 'bg-ocean-100 text-ocean-700'}`}>
                      {player.username[0]?.toUpperCase()}
                    </div>
                    <span className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                      {player.username}
                    </span>
                    <span className={`text-xs ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                      {player.points}分
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* 故事汤面（游戏中） */}
            {currentRoom.status === 'playing' && currentRoom.story && (
              <div className={`mb-4 p-4 rounded-2xl border-l-4 border-purple-500 ${isDark ? 'bg-purple-900/20' : 'bg-purple-50'}`}>
                <div className={`text-sm font-medium mb-1 ${isDark ? 'text-purple-300' : 'text-purple-700'}`}>汤面</div>
                <p className={`${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{currentRoom.story.surface}</p>
              </div>
            )}

            {/* 聊天区 */}
            <div className={`rounded-2xl ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white shadow-lg'}`}>
              <div className="h-80 overflow-y-auto p-4 space-y-3">
                {messages.map((msg, i) => (
                  <div key={i} className={`${msg.type === 'system' || msg.type === 'info' || msg.type === 'success' || msg.type === 'story' ? 'text-center' : ''}`}>
                    {msg.type === 'system' || msg.type === 'info' ? (
                      <span className={`inline-block px-3 py-1 rounded-full text-xs ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-500'}`}
                        dangerouslySetInnerHTML={{ __html: msg.content }}
                      />
                    ) : msg.type === 'success' ? (
                      <span className={`inline-block px-4 py-2 rounded-xl text-sm font-medium ${isDark ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700'}`}
                        dangerouslySetInnerHTML={{ __html: msg.content }}
                      />
                    ) : msg.type === 'story' ? (
                      <span className={`inline-block px-4 py-2 rounded-xl text-sm ${isDark ? 'bg-purple-900/40 text-purple-200' : 'bg-purple-100 text-purple-800'}`}
                        dangerouslySetInnerHTML={{ __html: msg.content }}
                      />
                    ) : (
                      <div className={`flex ${msg.username === currentUser?.username ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[75%] px-4 py-2 rounded-2xl ${
                          msg.username === currentUser?.username
                            ? 'bg-ocean-600 text-white'
                            : isDark ? 'bg-gray-700 text-gray-100' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {msg.username !== currentUser?.username && (
                            <div className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{msg.username}</div>
                          )}
                          <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* 输入区 */}
              <div className={`p-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    placeholder={currentRoom.status === 'playing' ? '提问或讨论...' : '聊天...'}
                    className={`flex-1 px-4 py-2.5 rounded-xl border ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'} focus:outline-none focus:ring-2 focus:ring-ocean-500/30`}
                  />
                  {currentRoom.status === 'playing' && (
                    <button
                      onClick={submitGuess}
                      disabled={!inputMessage.trim()}
                      className="px-4 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition font-medium disabled:opacity-40"
                    >
                      猜答案
                    </button>
                  )}
                  <button
                    onClick={sendMessage}
                    disabled={!inputMessage.trim()}
                    className="px-4 py-2.5 bg-ocean-600 text-white rounded-xl hover:bg-ocean-700 transition disabled:opacity-40"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default MultiplayerPage;
